import React, { ReactElement, useRef, useState } from 'react';
import { Close, ErrorOutline, ReplayRounded, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  gray10,
  gray40,
  gray60,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import { regularLg, regularSm, regularXl, regularXs } from '../../../constants/location';
import { MasterWithMappingType } from '../../../types/master';
import { resolveFilterKeys } from '../../../utils/master/matchKeys';
import { findFilterMismatch } from '../../../utils/master/filterConditions';
import {
  MasterCsvRow,
  MasterFilterCondition,
  MasterMatchResult,
} from '../../../types/masterReconciliation';
import {
  MasterFilterConditionRequest,
  MasterMatchCandidate,
  MasterHistoryRecord,
} from '../../../types/masterVerificationApi';
import {
  searchMasterReconcile,
  listMasterHistory,
  deleteMasterHistory,
} from '../../../api/firebase/function/handleMasterVerification';
import DeleteModal from './DeleteModal';

const styles = {
  // 絞り込み条件の表示（#3180）。どの条件で候補が絞られる設定かを検索の手前に示す。
  filterConditionBox: {
    backgroundColor: gray10,
    borderRadius: '4px',
    boxSizing: 'border-box',
    marginBottom: '12px',
    padding: '8px 12px',
  },
  filterConditionChip: {
    ...regularXs,
    alignItems: 'center',
    backgroundColor: white,
    border: '1px solid ' + gray40,
    borderRadius: '4px',
    color: gray100,
    display: 'inline-flex',
    gap: '4px',
    padding: '2px 4px 2px 8px',
  },
  // 外した絞り込み条件（#3441）。戻せるようチップは残し、効いていないことが分かる見た目にする
  filterConditionChipExcluded: {
    backgroundColor: gray10,
    borderStyle: 'dashed',
    color: gray60,
    textDecoration: 'line-through',
  },
  filterConditionChipButton: {
    color: gray80,
    padding: '2px',
  },
  filterConditionChipIcon: {
    fontSize: '14px',
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      padding: '32px',
      margin: '0',
      width: '70vw',
      '@media screen and (max-width:960px)': {
        padding: '16px',
        width: '100%',
        margin: '16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  dialogTitle: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  iconButton: {
    padding: 0,
  },
  dialogCloseButton: {
    height: '32px',
    width: '32px',
  },
  // 候補が多いと選択ボタンまでスクロールで見失うため、表の中だけをスクロールさせる。
  // ダイアログ（縦の flex）の残りの高さに収まるよう縮ませる。
  // minHeight に固定値を入れると、候補が少ないときに表の下へ空の枠が残るため 0 にする。
  tableContainer: {
    minWidth: '100%',
    minHeight: 0,
    overflow: 'auto',
    margin: '0px',
    padding: '0px',
    border: '1px solid #E0E0E0',
  },
  // スクロールしてもヘッダーを上に残す。照合履歴は見出しが2段あるため、
  // セル単位の stickyHeader ではなく thead ごと固定する。
  stickyTableHead: {
    backgroundColor: white,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    '& .MuiTableCell-root': {
      backgroundColor: white,
    },
  },
  tableHeader: {
    padding: '8px 10px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '35px',
    borderRight: '1px solid #E0E0E0',
  },
  tableHeaderHighlight: {
    padding: '8px 10px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 700,
    minWidth: '35px',
    borderRight: '1px solid #E0E0E0',
  },
  // 大項目ヘッダー（複数カラムをまたぐグループ見出し）（#2905）
  // 背景・縦罫を外し、下線だけで区切ってすっきり見せる。
  tableGroupHeader: {
    padding: '6px 10px',
    color: gray100,
    fontSize: '13px',
    fontWeight: 700,
    borderRight: 'none',
    borderBottom: `2px solid ${gray80}`,
  },
  // スコア / 照合理由など短いテキスト向けのヘッダー。横幅を最小限に詰める。
  tableHeaderNarrow: {
    padding: '8px 10px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    width: '1%',
    borderRight: '1px solid #E0E0E0',
  },
  // 絞り込み条件に合わない値（#3408）。アイコンと値を横並びにする
  mismatchValue: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: '4px',
  },
  mismatchIcon: {
    fontSize: '16px',
  },
  tableCell: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px',
    whiteSpace: 'nowrap',
    minWidth: '120px',
    borderRight: '1px solid #E0E0E0',
  },
  // スコア / 照合理由など短いテキスト向け。横幅を最小限に詰める。
  tableCellNarrow: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px',
    whiteSpace: 'nowrap',
    width: '1%',
    borderRight: '1px solid #E0E0E0',
  },
  buttonCell: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px',
    width: '44px',
    borderRight: '1px solid #E0E0E0',
  },
  selectButton: {
    display: 'flex',
    width: '44px',
    height: '32px',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: '16px',
  },
  // 照合履歴タブの操作セル（選択・削除ボタンを横並びで一番左に固定し、横スクロール不要にする）
  actionCell: {
    padding: '8px 10px',
    width: '1%',
    whiteSpace: 'nowrap',
    borderRight: '1px solid #E0E0E0',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  actionButton: {
    height: '32px',
    minWidth: '44px',
    whiteSpace: 'nowrap',
  },
  emptyMessage: {
    ...regularSm,
    color: gray80,
    textAlign: 'center',
    padding: '32px 0',
  },
  textField: {
    '& .MuiInputBase-root': { height: '40px', fontSize: '14px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
      borderWidth: '1px',
    },
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 0',
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (result: MasterMatchResult) => void;
  onUnlink?: () => void;
  master: MasterWithMappingType | null;
  /** FirestoreのcompanyId（gendrive_company_id） */
  companyId: string;
  /** 元のOCR読取値（履歴の query_text 用。初期検索クエリのフォールバックにも使う） */
  ocrValue?: string;
  /** AI照合の初期検索クエリ。入力欄で編集中の現在値。未指定なら ocrValue を使う。 */
  searchQuery?: string;
  /** スコープ（取引先など）の値。設定時は履歴照合をこのスコープで絞る。 */
  supplierId?: string;
  /**
   * 絞り込みキーの現在値（#3180）。
   * AI照合・部分一致の候補はこの条件で絞ってから取得する（#3441）。
   * 画面で外した条件は検索に使わない。照合履歴タブは一覧表示のため絞り込まない。
   */
  filterConditions?: MasterFilterCondition[];
}

const MasterReconciliationModal = ({
  open,
  onClose,
  onSelect,
  onUnlink,
  master,
  companyId,
  ocrValue = '',
  searchQuery,
  supplierId,
  filterConditions,
}: Props): ReactElement => {
  const [activeTab, setActiveTab] = useState(0);
  const [matchSearchWord, setMatchSearchWord] = useState('');
  const [keywordName, setKeywordName] = useState('');
  // 照合履歴タブの検索語（#3072）。履歴は全件表示せず、この語に類似するもののみ表示する。
  const [historyName, setHistoryName] = useState('');
  const [aiResults, setAiResults] = useState<MasterMatchCandidate[]>([]);
  const [keywordResults, setKeywordResults] = useState<MasterMatchCandidate[]>([]);
  const [historyList, setHistoryList] = useState<MasterHistoryRecord[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<MasterHistoryRecord | null>(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [loadingKeyword, setLoadingKeyword] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // 履歴検索を一度でも実行したか（#3308）。
  // 検索窓に文字を入れただけで「該当する照合履歴はありません」と出るのを防ぐ。
  const [historySearched, setHistorySearched] = useState(false);
  const [deletingHistory, setDeletingHistory] = useState(false);
  // 画面で外した絞り込み条件の列（#3441）。開き直すと空に戻す（外した状態は保存しない）。
  // 同じ列を複数の絞り込みが使うことはバリデーションで防いでいるため、列名をキーにする。
  const [excludedFilterColumns, setExcludedFilterColumns] = useState<string[]>([]);
  const aiSearchIdRef = useRef(0);
  const keywordSearchIdRef = useRef(0);

  // 検索に使う絞り込み条件（外した列を除く）。API の形（snake_case）に変換する。
  const buildActiveFilterConditions = (excludedColumns: string[]): MasterFilterConditionRequest[] =>
    (filterConditions ?? [])
      .filter(condition => !excludedColumns.includes(condition.csvColumn))
      .map(condition => ({
        csv_column: condition.csvColumn,
        value: condition.value,
        filter_match_mode: condition.filterMatchMode,
      }));

  // 絞り込みキーが対象にしているマスタ列（#3180）。
  // 候補が正しく絞られているかを目視で確認できるよう、一覧の左端に置く。
  const filterColumns = React.useMemo<string[]>(() => {
    if (!master) return [];
    const columns = resolveFilterKeys(master)
      .map(key => key.csv_column)
      .filter((col): col is string => !!col);
    // 同じ列を複数の絞り込みが使うことはバリデーションで防いでいるが、念のため重複を除く
    return Array.from(new Set(columns));
  }, [master]);

  // 並び順: 絞り込みキー → 照合先項目 → マッピング対象 → その他(残りカラム全て)。
  // 絞り込みキーを先頭に置くのは、どの条件で候補が絞られているかを確認しやすくするため（#3180）。
  // 「その他」は最初の候補の raw_data のキーから取得する(全候補で同じカラム構成の想定)。
  const csvHeaders = React.useMemo<string[]>(() => {
    if (!master) return [];
    const primary = master.search_csv_column
      ? [master.search_csv_column].filter(col => !filterColumns.includes(col))
      : [];
    const mapped = (master.column_mappings ?? [])
      .map(m => m.csv_column)
      .filter(col => col && col !== master.search_csv_column && !filterColumns.includes(col));
    const knownSet = new Set<string>([...filterColumns, ...primary, ...mapped]);
    const sampleKeys = aiResults[0]
      ? Object.keys(aiResults[0].raw_data ?? {})
      : keywordResults[0]
        ? Object.keys(keywordResults[0].raw_data ?? {})
        : historyList[0]
          ? Object.keys(historyList[0].raw_data ?? {})
          : [];
    const others = sampleKeys.filter(k => !knownSet.has(k));
    return [...filterColumns, ...primary, ...mapped, ...others];
  }, [master, filterColumns, aiResults, keywordResults, historyList]);

  // 照合履歴と一緒に記録したスコープ値（supplier_id）の列を出すか（#2905）。
  // スコープ未設定マスタでは全件空のため列を出さず、記録がある場合のみ表示する。
  const showSupplierColumn = React.useMemo<boolean>(
    () => historyList.some(record => !!record.supplier_id),
    [historyList],
  );

  // テーブルヘッダーは「マスタCSVのカラム名そのもの」を表示する。
  // 旧実装では照合先/マッピング列だけOCR項目側ラベル(例: 注文情報(納入先) > 企業名)に
  // 差し替えていたが、ユーザー目線ではマスタの生カラム名のほうが直感的なので統一。
  const getHeaderLabel = (header: string): string => header;

  // マスタ情報のセル。絞り込み条件に合わない値は赤字＋注意アイコンにする（#3408）。
  // 判定は外した条件も含めた全条件で行う（#3441）。AI照合・部分一致は効いている条件で
  // 絞り込み済みのため、赤字になるのは外した条件に合わない値だけになる。
  // 照合履歴は絞り込まないため、従来どおり全条件で判定する。
  const renderDataCell = (
    rawData: MasterCsvRow,
    header: string,
    colIndex: number,
  ): ReactElement => {
    const mismatch = findFilterMismatch(rawData, header, filterConditions);
    if (!mismatch) {
      return (
        <TableCell key={colIndex} sx={styles.tableCell}>
          {rawData[header] ?? ''}
        </TableCell>
      );
    }
    return (
      <TableCell key={colIndex} sx={{ ...styles.tableCell, color: red50 }}>
        <Tooltip
          title={`絞り込み条件（${mismatch.itemLabel}：${mismatch.value}）と一致しません`}
          arrow
          placement="top"
        >
          <Box sx={styles.mismatchValue}>
            <ErrorOutline sx={styles.mismatchIcon} />
            {rawData[header] ?? ''}
          </Box>
        </Tooltip>
      </TableCell>
    );
  };

  // 「照合先項目」「マッピング項目」を薄色でハイライトする案は一旦見送り、色分けはしない。
  // 必要になったら master.search_csv_column / column_mappings の csv_column を
  // 参照して TableCell の sx に backgroundColor を足す形で復活可能。

  // モーダルが開いたとき、初期クエリ（入力欄の編集中の値。未指定ならOCR値）でAI照合を自動実行
  React.useEffect(() => {
    if (!open) return;
    const initialQuery = searchQuery ?? ocrValue;
    setMatchSearchWord(initialQuery);
    // 部分一致タブの検索窓にも初期クエリを入れておき、タブを開いてすぐ検索できるようにする。
    setKeywordName(initialQuery);
    // 照合履歴タブも同様。初期クエリで絞り込んだ履歴を表示する（#3072）。
    setHistoryName(initialQuery);
    // 照合方式が部分一致(keyword)のマッピングなら、部分一致タブ(index 1)を初期表示にする（#3022）。
    // それ以外（AI照合／未設定）は従来通り AI照合タブ(index 0)。
    setActiveTab(master?.match_mode === 'keyword' ? 1 : 0);
    setAiResults([]);
    setKeywordResults([]);
    setHistoryList([]);
    setHistorySearched(false);
    setDeleteTarget(null);
    setExcludedFilterColumns([]);

    if (master && companyId) {
      // タブ切替時に待たせないよう、モーダルを開いた時点で全タブぶん先読みする。
      // 開き直した直後は外した条件が無いため、全条件で絞り込む。
      if (initialQuery) {
        const initialConditions = buildActiveFilterConditions([]);
        void handleAiSearch(initialQuery, initialConditions);
        void handleKeywordSearch(initialQuery, initialConditions);
        // 履歴は検索語必須（#3072）。初期クエリが無い場合は取得しない。
        void loadHistoryList(initialQuery);
      }
    }
  }, [open]);

  // タブを切り替えたとき、直前のタブで入力していた検索語を移動先の検索窓へ引き継ぐ（#3274）。
  // タブごとに打ち直す手間をなくすのが目的。検索自体は自動実行せず、
  // 移動先の検索ボタン／Enter で実行する（タブ切替のたびにAPIを叩かないため）。
  const handleTabChange = (_: React.SyntheticEvent, newValue: number): void => {
    const currentWord = [matchSearchWord, keywordName, historyName][activeTab] ?? '';
    if (currentWord) {
      if (newValue === 0) setMatchSearchWord(currentWord);
      else if (newValue === 1) setKeywordName(currentWord);
      else if (newValue === 2) setHistoryName(currentWord);
    }
    setActiveTab(newValue);
  };

  // conditions は state 更新を待たずに再検索するとき（#3441）に渡す。省略時は今の状態から作る。
  const handleAiSearch = async (
    query: string,
    conditions: MasterFilterConditionRequest[] = buildActiveFilterConditions(excludedFilterColumns),
  ): Promise<void> => {
    if (!master || !companyId || !query) return;
    aiSearchIdRef.current += 1;
    const requestId = aiSearchIdRef.current;
    setLoadingAi(true);
    try {
      const res = await searchMasterReconcile({
        gendrive_company_id: companyId,
        source_field_name: master.search_item_label,
        query_text: query,
        top_k: 20,
        pattern_id: master.pattern_id,
        // 履歴照合をスコープ（取引先など）で絞る
        supplier_id: supplierId,
        filter_conditions: conditions,
      });
      if (requestId !== aiSearchIdRef.current) return;
      setAiResults(res.results);
    } catch {
      if (requestId !== aiSearchIdRef.current) return;
      setAiResults([]);
    } finally {
      if (requestId === aiSearchIdRef.current) setLoadingAi(false);
    }
  };

  const handleKeywordSearch = async (
    query?: string,
    conditions: MasterFilterConditionRequest[] = buildActiveFilterConditions(excludedFilterColumns),
  ): Promise<void> => {
    // 引数があればそれを、無ければ検索窓の現在値（keywordName）を使う。
    const q = query ?? keywordName;
    if (!master || !companyId || !q) return;
    // 絞り込み条件を素早く外す・戻すと応答の順番が入れ替わるため、最後の検索の応答だけ採用する（#3441）。
    keywordSearchIdRef.current += 1;
    const requestId = keywordSearchIdRef.current;
    setLoadingKeyword(true);
    try {
      const res = await searchMasterReconcile({
        gendrive_company_id: companyId,
        source_field_name: master.search_item_label,
        query_text: q,
        top_k: 20,
        pattern_id: master.pattern_id,
        mode: 'keyword',
        // 自動照合と挙動を揃えるため、部分一致は search_csv_column（raw_data の列）に ILIKE する（#3022）。
        target_column: master.search_csv_column,
        filter_conditions: conditions,
      });
      if (requestId !== keywordSearchIdRef.current) return;
      setKeywordResults(res.results);
    } catch {
      if (requestId !== keywordSearchIdRef.current) return;
      setKeywordResults([]);
    } finally {
      if (requestId === keywordSearchIdRef.current) setLoadingKeyword(false);
    }
  };

  // 絞り込み条件を外す・戻す（#3441）。自動照合で得意先コード等を誤読したときに、
  // 条件を外して探せるようにする。結果と条件がずれないよう、両タブを各検索窓の値で再検索する。
  const toggleFilterCondition = (csvColumn: string): void => {
    const nextExcluded = excludedFilterColumns.includes(csvColumn)
      ? excludedFilterColumns.filter(column => column !== csvColumn)
      : [...excludedFilterColumns, csvColumn];
    setExcludedFilterColumns(nextExcluded);
    const conditions = buildActiveFilterConditions(nextExcluded);
    void handleAiSearch(matchSearchWord, conditions);
    void handleKeywordSearch(keywordName, conditions);
  };

  // 検索語に類似する照合履歴のみを取得する（#3072）。
  // 全件表示は照合データの抜き取りにつながるため行わない。
  const loadHistoryList = async (query?: string): Promise<void> => {
    // 引数があればそれを、無ければ検索窓の現在値（historyName）を使う。
    const q = query ?? historyName;
    if (!master || !companyId) return;
    // 検索語が空なら前回の結果を残さずクリアする（#3072）。
    // 残したままだと、検索窓を空にしても直前の履歴が表示され続けてしまう。
    if (!q) {
      setHistoryList([]);
      setHistorySearched(false);
      return;
    }
    setHistorySearched(true);
    setLoadingHistory(true);
    try {
      const res = await listMasterHistory({
        gendrive_company_id: companyId,
        source_field_name: master.search_item_label,
        query_text: q,
      });
      setHistoryList(res.results);
    } catch {
      setHistoryList([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // 履歴の行を選択 → フォームに反映（照合履歴に完全一致として扱う）
  const handleHistorySelect = (record: MasterHistoryRecord): void => {
    onSelect({
      rowData: record.raw_data,
      matchedValue: record.embedding_text,
      score: '100',
      matchReason: 'confirmed',
      matchReasonLabel: '照合履歴に完全一致',
    });
  };

  // 削除確認ダイアログで「削除する」を押したときの実処理
  const handleHistoryDelete = async (): Promise<void> => {
    if (!master || !companyId || !deleteTarget) return;
    setDeletingHistory(true);
    try {
      await deleteMasterHistory({
        gendrive_company_id: companyId,
        source_field_name: master.search_item_label,
        history_id: deleteTarget.history_id,
      });
      setDeleteTarget(null);
      await loadHistoryList();
    } finally {
      setDeletingHistory(false);
    }
  };

  /** MasterMatchCandidate → MasterMatchResult に変換して onSelect に渡す */
  const handleCandidateSelect = (candidate: MasterMatchCandidate): void => {
    // 照合方式が部分一致のマッピングは、どのタブから選んでも照合先列（search_csv_column）の
    // 実値を表示する（#3028）。embedding_text は embedding 済み列（例: 商品識別コード）由来のため、
    // 別列（例: JANコード）を照合先に設定していても embedding 側の値が出てしまうのを防ぐ。
    const matchedValue =
      master?.match_mode === 'keyword' && master?.search_csv_column
        ? (candidate.raw_data[master.search_csv_column] ?? candidate.embedding_text)
        : candidate.embedding_text;
    onSelect({
      rowData: candidate.raw_data,
      matchedValue,
      score: String(candidate.confidence),
      matchReason: candidate.match_reason,
      matchReasonLabel: candidate.match_reason_label,
      masterRecordId: candidate.master_record_id,
      // どの条件を外して探した候補かを、照合理由の表示用に呼び出し側へ渡す（#3517）
      excludedFilterColumns: [...excludedFilterColumns],
    });
  };

  return (
    <Dialog open={open} sx={styles.dialog} maxWidth={false} fullWidth={true}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>
          {master?.file_name || 'マスタ'} - マスタ照合
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {onUnlink && (
            <Button
              sx={{ ...secondaryButtonBackgroundColor, height: '32px', whiteSpace: 'nowrap' }}
              onClick={onUnlink}
            >
              照合解除
            </Button>
          )}
          <IconButton onClick={onClose} sx={styles.iconButton}>
            <Close sx={styles.dialogCloseButton} />
          </IconButton>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          mb: '16px',
          borderBottom: '1px solid #E0E0E0',
          '& .MuiTabs-indicator': { backgroundColor: gray100 },
          '& .MuiTab-root.Mui-selected': { color: gray100 },
          '& .MuiTab-root': { color: gray80 },
        }}
      >
        <Tab label="AI照合" />
        <Tab label="部分一致検索" />
        <Tab label="照合履歴" />
      </Tabs>

      {/* 絞り込み条件（#3180）。どの条件で候補が絞られているかを検索欄の手前に示す。
          全タブ共通で表示する（AI照合・部分一致・履歴のいずれでも設定は同じため）。
          × で条件を外して検索し、外したチップの ↺ で戻せる（#3441）。 */}
      {filterConditions && filterConditions.length > 0 && (
        <Box sx={styles.filterConditionBox}>
          <Typography sx={{ ...regularXs, color: gray80, mb: '4px' }}>絞り込み条件</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {filterConditions.map(condition => {
              const excluded = excludedFilterColumns.includes(condition.csvColumn);
              return (
                <Box
                  key={condition.csvColumn}
                  sx={[styles.filterConditionChip, excluded && styles.filterConditionChipExcluded]}
                >
                  {condition.itemLabel}：{condition.value}
                  <Tooltip
                    title={excluded ? 'この条件を戻して検索' : 'この条件を外して検索'}
                    arrow
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      sx={styles.filterConditionChipButton}
                      onClick={(): void => toggleFilterCondition(condition.csvColumn)}
                    >
                      {excluded ? (
                        <ReplayRounded sx={styles.filterConditionChipIcon} />
                      ) : (
                        <Close sx={styles.filterConditionChipIcon} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
          {excludedFilterColumns.length > 0 && (
            <Typography sx={{ ...regularXs, color: gray80, mt: '4px' }}>
              外した条件は検索に使いません。条件に合わない値は赤字で表示します。
            </Typography>
          )}
        </Box>
      )}

      {/* Tab 0: AI照合 */}
      {activeTab === 0 && (
        <>
          <Typography sx={{ ...regularXs, color: gray80, mb: '12px' }}>
            入力された名称をもとにAIが候補を絞り込みます。該当する結果を選択してください。
          </Typography>
          <Box sx={{ display: 'flex', gap: '12px', mb: '16px', alignItems: 'flex-end' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <Typography sx={{ ...regularSm, color: gray80 }}>
                {master?.search_item_label ?? '名前'}（類似一致）
              </Typography>
              <TextField
                size="small"
                value={matchSearchWord}
                onChange={(e): void => setMatchSearchWord(e.target.value)}
                onKeyDown={(e): void => {
                  if (e.key === 'Enter') void handleAiSearch(matchSearchWord);
                }}
                placeholder="例: 東亜"
                sx={styles.textField}
              />
            </Box>
            <Button
              size="small"
              startIcon={<Search />}
              sx={{ ...primaryButtonBackgroundColor, height: '40px' }}
              onClick={(): void => void handleAiSearch(matchSearchWord)}
              disabled={loadingAi}
            >
              再照合
            </Button>
          </Box>
          {loadingAi ? (
            <Box sx={styles.loadingWrapper}>
              <CircularProgress size={32} sx={{ color: gray80 }} />
            </Box>
          ) : aiResults.length === 0 ? (
            <Typography sx={styles.emptyMessage}>候補が見つかりませんでした</Typography>
          ) : (
            <TableContainer sx={styles.tableContainer}>
              <Table>
                <TableHead sx={styles.stickyTableHead}>
                  <TableRow>
                    <TableCell sx={styles.tableHeader}></TableCell>
                    <TableCell sx={styles.tableHeaderNarrow}>スコア</TableCell>
                    <TableCell sx={styles.tableHeaderNarrow}>照合理由</TableCell>
                    {csvHeaders.map((header, index) => (
                      <TableCell key={index} sx={styles.tableHeader}>
                        {getHeaderLabel(header)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aiResults.map((candidate, index) => (
                    <TableRow key={index}>
                      <TableCell align="center" sx={styles.buttonCell}>
                        <Button
                          sx={[secondaryButtonBackgroundColor, styles.selectButton]}
                          onClick={(): void => handleCandidateSelect(candidate)}
                        >
                          選択
                        </Button>
                      </TableCell>
                      <TableCell
                        sx={styles.tableCellNarrow}
                      >{`${candidate.confidence}%`}</TableCell>
                      <TableCell sx={styles.tableCellNarrow}>
                        {/* AIが選び直した候補には印を出す（#3355）。
                            スコアが2番手以下でも先頭に来るため、理由が分からないと混乱する */}
                        {candidate.llm_selected
                          ? `${candidate.match_reason_label}（AI選択）`
                          : candidate.match_reason_label}
                      </TableCell>
                      {csvHeaders.map((header, colIndex) =>
                        renderDataCell(candidate.raw_data, header, colIndex),
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Tab 1: 部分一致検索 */}
      {activeTab === 1 && (
        <>
          <Typography sx={{ ...regularXs, color: gray80, mb: '12px' }}>
            キーワードを入力してマスタを部分一致で検索できます。該当する結果を選択してください。
          </Typography>
          <Box sx={{ display: 'flex', gap: '12px', mb: '16px', alignItems: 'flex-end' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <Typography sx={{ ...regularSm, color: gray80 }}>
                {master?.search_item_label ?? '名前'}（部分一致）
              </Typography>
              <TextField
                size="small"
                value={keywordName}
                onChange={(e): void => setKeywordName(e.target.value)}
                onKeyDown={(e): void => {
                  if (e.key === 'Enter') void handleKeywordSearch();
                }}
                placeholder="例: 東亜"
                sx={styles.textField}
              />
            </Box>
            <Button
              startIcon={<Search />}
              sx={{ ...primaryButtonBackgroundColor, height: '40px', whiteSpace: 'nowrap' }}
              onClick={(): void => void handleKeywordSearch()}
              disabled={loadingKeyword}
            >
              検索
            </Button>
          </Box>
          {loadingKeyword ? (
            <Box sx={styles.loadingWrapper}>
              <CircularProgress size={32} sx={{ color: gray80 }} />
            </Box>
          ) : keywordResults.length === 0 ? (
            <Typography sx={styles.emptyMessage}>
              {keywordName ? '候補が見つかりませんでした' : 'キーワードを入力して検索してください'}
            </Typography>
          ) : (
            <TableContainer sx={styles.tableContainer}>
              <Table>
                <TableHead sx={styles.stickyTableHead}>
                  <TableRow>
                    <TableCell sx={styles.tableHeader}></TableCell>
                    {csvHeaders.map((header, index) => (
                      <TableCell key={index} sx={styles.tableHeader}>
                        {getHeaderLabel(header)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keywordResults.map((candidate, index) => (
                    <TableRow key={index}>
                      <TableCell align="center" sx={styles.buttonCell}>
                        <Button
                          sx={[secondaryButtonBackgroundColor, styles.selectButton]}
                          onClick={(): void => handleCandidateSelect(candidate)}
                        >
                          選択
                        </Button>
                      </TableCell>
                      {csvHeaders.map((header, colIndex) =>
                        renderDataCell(candidate.raw_data, header, colIndex),
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Tab 2: 照合履歴（検索語に類似する履歴の一覧・選択反映・削除。#3072） */}
      {activeTab === 2 && (
        <>
          <Typography sx={{ ...regularXs, color: gray80, mb: '12px' }}>
            この項目で過去に紐づけたマスタの履歴のうち、検索した名称に近いものを表示します。行を選択すると反映できます。誤った紐づけは削除してください。
          </Typography>
          <Box sx={{ display: 'flex', gap: '12px', mb: '16px', alignItems: 'flex-end' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <Typography sx={{ ...regularSm, color: gray80 }}>
                {master?.search_item_label ?? '名前'}（履歴を検索）
              </Typography>
              <TextField
                size="small"
                value={historyName}
                onChange={(e): void => setHistoryName(e.target.value)}
                onKeyDown={(e): void => {
                  if (e.key === 'Enter') void loadHistoryList();
                }}
                placeholder="例: 東亜"
                sx={styles.textField}
              />
            </Box>
            <Button
              size="small"
              startIcon={<Search />}
              sx={{ ...primaryButtonBackgroundColor, height: '40px' }}
              onClick={(): void => void loadHistoryList()}
              disabled={loadingHistory}
            >
              検索
            </Button>
          </Box>
          {loadingHistory ? (
            <Box sx={styles.loadingWrapper}>
              <CircularProgress size={32} sx={{ color: gray80 }} />
            </Box>
          ) : historyList.length === 0 ? (
            <Typography sx={styles.emptyMessage}>
              {historySearched ? '該当する照合履歴はありません' : '名称を入力して検索してください'}
            </Typography>
          ) : (
            <TableContainer sx={styles.tableContainer}>
              <Table>
                <TableHead sx={styles.stickyTableHead}>
                  {/* 大項目ヘッダー（#2905）: 入力由来のキーとマスタ情報を区切る */}
                  <TableRow>
                    <TableCell sx={styles.tableHeaderNarrow}></TableCell>
                    <TableCell
                      align="left"
                      colSpan={1 + (showSupplierColumn ? 1 : 0)}
                      sx={{ ...styles.tableGroupHeader, borderRight: '1px solid #E0E0E0' }}
                    >
                      読取・記録キー
                    </TableCell>
                    {csvHeaders.length > 0 && (
                      <TableCell
                        align="left"
                        colSpan={csvHeaders.length}
                        sx={styles.tableGroupHeader}
                      >
                        マスタ情報
                      </TableCell>
                    )}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableHeaderNarrow}></TableCell>
                    <TableCell sx={styles.tableHeaderHighlight}>OCR読取値</TableCell>
                    {/* 照合履歴と一緒に記録したスコープ値（取引先コード等）。記録がある場合のみ表示（#2905） */}
                    {showSupplierColumn && (
                      <TableCell sx={styles.tableHeaderHighlight}>
                        {master?.scope_item_label || '記録した値'}
                      </TableCell>
                    )}
                    {/* マスタキー（csvHeaders 先頭 = search_csv_column）のみ太字、取得項目は通常 */}
                    {csvHeaders.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={index === 0 ? styles.tableHeaderHighlight : styles.tableHeader}
                      >
                        {getHeaderLabel(header)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyList.map((record, index) => (
                    <TableRow key={index}>
                      <TableCell sx={styles.actionCell}>
                        <Box sx={styles.actionButtons}>
                          <Button
                            sx={[secondaryButtonBackgroundColor, styles.actionButton]}
                            onClick={(): void => handleHistorySelect(record)}
                          >
                            選択
                          </Button>
                          <Button
                            sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
                            onClick={(): void => setDeleteTarget(record)}
                          >
                            削除
                          </Button>
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.tableCell}>{record.query_text}</TableCell>
                      {showSupplierColumn && (
                        <TableCell sx={styles.tableCell}>{record.supplier_id || ''}</TableCell>
                      )}
                      {csvHeaders.map((header, colIndex) =>
                        renderDataCell(record.raw_data, header, colIndex),
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <DeleteModal
        open={deleteTarget !== null}
        onClose={(): void => setDeleteTarget(null)}
        onDelete={(): void => void handleHistoryDelete()}
        title="照合履歴"
        loading={deletingHistory}
        description={
          deleteTarget
            ? `OCR読取値「${deleteTarget.query_text}」とマスタ「${deleteTarget.embedding_text}」の紐づけ履歴を削除します。よろしいですか？`
            : undefined
        }
      />
    </Dialog>
  );
};

export default MasterReconciliationModal;
